import { todayStr, toLocalDateStr } from "../lib/date";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getStudents, getGroupLists, getDistricts, getStaff, getSemesters } from "../lib/api";
import { useAuth } from "../App";
import { DrillLogSection } from "../components/calendar/SchoolOpsSection";

import SubmitReportTab from "../components/reports/SubmitReportTab";
import StudentAttendanceTab from "../components/reports/StudentAttendanceTab";
import DailyAttendanceSummaryTab from "../components/reports/DailyAttendanceSummaryTab";
import StudentHoursTab from "../components/reports/StudentHoursTab";
import AtRiskTab from "../components/reports/AtRiskTab";
import GroupListsTab from "../components/reports/GroupListsTab";
import SignupRostersTab from "../components/reports/SignupRostersTab";
import PhoneListTab from "../components/reports/PhoneListTab";
import SnapshotReportTab from "../components/reports/SnapshotReportTab";
import AssessmentReportTab from "../components/reports/AssessmentReportTab";
import MedicalTab from "../components/reports/MedicalTab";
import BillingTab from "../components/reports/BillingTab";
import QaDataTab from "../components/reports/QaDataTab";
import RiskAssessmentTab from "../components/reports/RiskAssessmentTab";
import FormExpirationsTab from "../components/reports/FormExpirationsTab";
import ScheduleReviewsTab from "../components/reports/ScheduleReviewsTab";
import IncidentReportsTab from "../components/reports/IncidentReportsTab";
import GroupDisruptionsTab from "../components/reports/GroupDisruptionsTab";
import BullyingMonitorTab from "../components/reports/BullyingMonitorTab";

export default function ReportsPage() {
  const { user, isAdmin, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const activeTab = urlTab || "create";
  const setActiveTab = (v) => {
    if (v === "create") setSearchParams({}, { replace: true });
    else setSearchParams({ tab: v }, { replace: true });
  };
  const [students, setStudents] = useState([]);
  const [reportStudents, setReportStudents] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [semesterDates, setSemesterDates] = useState([]);
  const [formerStudents, setFormerStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Shared date range
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toLocalDateStr(d);
  });
  const [endDate, setEndDate] = useState(todayStr());
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterStudent, setFilterStudent] = useState("all");

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [studentsRes, districtsRes, staffRes, semRes] = await Promise.all([
          getStudents("current"),
          getDistricts(),
          getStaff(),
          getSemesters().catch(() => ({ data: { semesters: [] } })),
        ]);
        setStudents(studentsRes.data);
        // Full current roster (year-round, incl. non-summer-school students) for the
        // incident/clinical/accident report pickers — a student can have a report
        // filed regardless of summer enrollment.
        try {
          const allCurrentRes = await getStudents("current", true);
          setReportStudents(allCurrentRes.data);
        } catch { setReportStudents(studentsRes.data); }
        setDistricts([...(districtsRes.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setStaffList(staffRes.data.sort((a, b) => a.name.localeCompare(b.name)));
        setSemesterDates(semRes.data.semesters || []);
        try {
          const formerRes = await getStudents("former");
          setFormerStudents(formerRes.data);
        } catch { /* no former students */ }
      } catch {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">Generate and view various reports</p>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-600 inline-block" /> Submit reports</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-600 inline-block" /> Summary reports</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-600 inline-block" /> Printables (post on the wall)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-700 inline-block" /> Admin / compliance</span>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          {/* Group 1 — Submitting reports (blue) */}
          <TabsTrigger value="create" data-testid="tab-create-report" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Submit a Report</TabsTrigger>
          <TabsTrigger value="medical" data-testid="tab-medical" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">OTC / Medical</TabsTrigger>
          <TabsTrigger value="risk-assessment" data-testid="tab-risk-assessment" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Risk Assessment</TabsTrigger>

          {/* Group 2 — Summary reports (green) */}
          <TabsTrigger value="student-attendance" data-testid="tab-student-attendance" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Student Attendance</TabsTrigger>
          <TabsTrigger value="director-daily" data-testid="tab-director-daily" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Summary Chart</TabsTrigger>
          <TabsTrigger value="director-students" data-testid="tab-director-students" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Student Hours</TabsTrigger>
          <TabsTrigger value="snapshot" data-testid="tab-snapshot" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Snapshot Report</TabsTrigger>
          <TabsTrigger value="at-risk" data-testid="tab-at-risk" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">At Risk</TabsTrigger>
          <TabsTrigger value="schedule-reviews" data-testid="tab-schedule-reviews" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Schedule Reviews</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments-report" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Assessments</TabsTrigger>

          {/* Group 3 — Printables (violet) */}
          <TabsTrigger value="group-lists" data-testid="tab-group-lists" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Group Lists</TabsTrigger>
          {(isAdmin() || hasRole("Clinician")) && <TabsTrigger value="incident-reports" data-testid="tab-incident-reports" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Incident Reports</TabsTrigger>}
          {(isAdmin() || hasRole("Clinician")) && <TabsTrigger value="group-disruptions" data-testid="tab-group-disruptions" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Group Disruptions</TabsTrigger>}
          <TabsTrigger value="signup-rosters" data-testid="tab-signup-rosters" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Sign-Up Rosters</TabsTrigger>
          <TabsTrigger value="phone-list" data-testid="tab-phone-list" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Phone List</TabsTrigger>

          {/* Group 4 — Admin / compliance (slate); Safety Drills moved to the very end */}
          {isAdmin() && <TabsTrigger value="billing" data-testid="tab-billing" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Billing</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="qa-data" data-testid="tab-qa-data" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">QA Data</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="form-expirations" data-testid="tab-form-expirations" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Form Expirations</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="bullying-monitor" data-testid="tab-bullying-monitor" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Bullying Monitor</TabsTrigger>}
          <TabsTrigger value="safety-drills" data-testid="tab-safety-drills" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Safety Drills</TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="space-y-6">
          <SubmitReportTab students={reportStudents} staffList={staffList} user={user} />
        </TabsContent>

        <TabsContent value="student-attendance" className="space-y-6">
          <StudentAttendanceTab students={students} districts={districts} startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </TabsContent>

        <TabsContent value="director-daily" className="space-y-6">
          <DailyAttendanceSummaryTab startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </TabsContent>

        <TabsContent value="director-students" className="space-y-6">
          <StudentHoursTab startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </TabsContent>

        <TabsContent value="at-risk" className="space-y-6">
          <AtRiskTab startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </TabsContent>

        <TabsContent value="schedule-reviews" className="space-y-6">
          <ScheduleReviewsTab />
        </TabsContent>

        <TabsContent value="risk-assessment" className="space-y-6">
          <RiskAssessmentTab />
        </TabsContent>

        <TabsContent value="group-lists" className="space-y-6">
          <GroupListsTab />
        </TabsContent>

        {(isAdmin() || hasRole("Clinician")) && (
          <TabsContent value="incident-reports" className="space-y-6">
            <IncidentReportsTab students={reportStudents} formerStudents={formerStudents} staffList={staffList} />
          </TabsContent>
        )}

        {(isAdmin() || hasRole("Clinician")) && (
          <TabsContent value="group-disruptions" className="space-y-6">
            <GroupDisruptionsTab />
          </TabsContent>
        )}

        <TabsContent value="signup-rosters" className="space-y-6">
          <SignupRostersTab />
        </TabsContent>

        <TabsContent value="phone-list" className="space-y-6">
          <PhoneListTab students={students} />
        </TabsContent>

        <TabsContent value="snapshot" className="space-y-6">
          <SnapshotReportTab students={students} formerStudents={formerStudents} districts={districts} semesterDates={semesterDates} />
        </TabsContent>

        <TabsContent value="assessments" className="space-y-6">
          <AssessmentReportTab students={students} formerStudents={formerStudents} districts={districts} startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </TabsContent>

        <TabsContent value="medical" className="space-y-6">
          <MedicalTab students={reportStudents} startDate={startDate} endDate={endDate} />
        </TabsContent>

        {isAdmin() && (
          <TabsContent value="billing" className="space-y-6">
            <BillingTab districts={districts} startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} filterDistrict={filterDistrict} setFilterDistrict={setFilterDistrict} />
          </TabsContent>
        )}

        {isAdmin() && (
          <TabsContent value="qa-data" className="space-y-6">
            <QaDataTab />
          </TabsContent>
        )}

        {isAdmin() && (
          <TabsContent value="form-expirations" className="space-y-6">
            <FormExpirationsTab />
          </TabsContent>
        )}

        {isAdmin() && (
          <TabsContent value="bullying-monitor" className="space-y-6">
            <BullyingMonitorTab staffList={staffList} students={reportStudents} formerStudents={formerStudents} />
          </TabsContent>
        )}

        <TabsContent value="safety-drills" className="space-y-6">
          <DrillLogSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
